import { PressableScale } from "pressto";
import { View, Text, Card } from "@src/components";
import { useUnistyles } from "react-native-unistyles";
import { DiagonalStripeSquare } from "@src/features/schedules/components/DiagonalStripeSquare";
import type { AlarmOverride } from "@src/services/api/types";
import { dateTimeRecordToLocalisedDateTime } from "@visioni/schedules";
import { formatTime12 } from "@src/utils/format-date";
import { format, isToday, isTomorrow } from "date-fns";
import { Icon, type IconNameType } from "@src/components/Icon";
import { AlarmStateBadge } from "./AlarmStateBadge";
import { capitalize } from "@src/utils/capitalize";
import { FC, memo } from "react";

interface OverridePreviewProps {
  override: AlarmOverride;
  timezone: string;
  onPress?: () => void;
  onDelete?: () => void;
  accessoryIcon?: IconNameType;
}

function formatOverridePeriod(
  override: AlarmOverride,
  timezone: string,
): { time: string; date: string; indefinite: string | null } {
  const startDateTime = dateTimeRecordToLocalisedDateTime(override.period.start, timezone);
  const startDate = startDateTime.toJSDate();

  const startTimeStr = formatTime12(startDateTime.hour, startDateTime.minute);

  if (override.period.end) {
    const endDateTime = dateTimeRecordToLocalisedDateTime(override.period.end, timezone);
    const endDate = endDateTime.toJSDate();
    const endTimeStr = formatTime12(endDateTime.hour, endDateTime.minute);

    const getDateStr = (date: Date) => {
      if (isToday(date)) return "today";
      if (isTomorrow(date)) return "tomorrow";
      return format(date, "MMM d");
    };

    const startDateStr = getDateStr(startDate);
    const endDateStr = getDateStr(endDate);

    if (startDateStr === endDateStr) {
      return {
        time: `${startTimeStr} - ${endTimeStr}`,
        date: capitalize(startDateStr),
        indefinite: null,
      };
    }

    return {
      time: `${startTimeStr} - ${endTimeStr}`,
      date: `${capitalize(startDateStr)} - ${capitalize(endDateStr)}`,
      indefinite: null,
    };
  }

  // Indefinite override
  const startDateStr =
    isToday(startDate) ? "today"
    : isTomorrow(startDate) ? "tomorrow"
    : format(startDate, "MMM d");
  return {
    time: startTimeStr,
    date: capitalize(startDateStr),
    indefinite: "(indefinite)",
  };
}

export const OverridePreview: FC<OverridePreviewProps> = memo(
  ({ override, timezone, onPress, onDelete, accessoryIcon }) => {
    const { theme } = useUnistyles();
    const { time, date, indefinite } = formatOverridePeriod(override, timezone);
    const overrideState = override.action === "armed" ? "armed" : "disarmed";

    const cardContent = (
      <Card style={{ borderColor: theme.colors.black.default }}>
        <View flexDirection="row" justifyContent="space-between">
          <View flex={1}>
            <View flexDirection="row" alignItems="center" gap="md" mb="2xl">
              <DiagonalStripeSquare state={overrideState} size={20} />
              <Text textStyle="sm/medium">Manual override</Text>
              <AlarmStateBadge state={overrideState} />
            </View>
            <View gap="sm" flexDirection="row" alignItems="center">
              <View flexDirection="row" alignItems="center" gap="xs">
                <Icon name="calendarOutline" color={theme.colors.textMuted} size={16} />
                <Text textStyle="xs/regular" color="textMuted">
                  {date}
                </Text>
              </View>
              <Text textStyle="xs/regular" color="textMuted">
                •
              </Text>
              <View flexDirection="row" alignItems="center" gap="xs">
                <Icon name="clockOutline" color={theme.colors.textMuted} size={16} />
                <Text textStyle="xs/regular" color="textMuted">
                  {time} {indefinite}
                </Text>
              </View>
            </View>
          </View>
          <View flexDirection="column" gap="md" alignItems="center">
            {onDelete && (
              <PressableScale onPress={onDelete} hitSlop={8}>
                <Icon name="delete" color={theme.colors.icon} />
              </PressableScale>
            )}
            {onPress && accessoryIcon && <Icon name={accessoryIcon} color={theme.colors.icon} />}
          </View>
        </View>
      </Card>
    );

    if (onPress) {
      return <PressableScale onPress={onPress}>{cardContent}</PressableScale>;
    }

    return cardContent;
  },
);
